import { FullScreenModal, Show } from '@sanlam-glacier/app-common/components';
import styles from './statement-modal.module.scss';
import { StatementTypes } from 'modules/clients/types/client.enum';
import { TransactionSummaryStatement } from '../statements/transaction-summary/TransactionSummaryStatement';
import { TransactionDetailStatement } from '../statements/transaction-detail/TransactionDetailStatement';
import { FundDetailStatement } from '../statements/fund-detail/FundDetailStatement';
import { InternalRateOfReturnInvestmentStatement } from '../statements/internal-rate-of-return-investment/InternalRateOfReturnInvestmentStatement';
import { ClientResponseModelMock } from 'modules/clients/types/client.interfaces';
import { SomethingWentWrong } from 'modules/app/components/pages/something-went-wrong/SomethingWentWrong';
import { getStartAndEndDate } from 'modules/clients/utils/statement-period-preset-helpers';
import { StatementFormValues } from 'modules/clients/types/client.types';
import TakeoverModalPageHeader from 'components/atoms/takeover-modal-page-header/TakeoverModalPageHeader';
import { downloadDocumentBackgroundFromBlob } from 'utils/background-promises/application-download';
import { useGetTransactionSummaryDownload } from 'bff/hooks/reporting/TransactionSummaryApi';
import { useGetTransactionDetailDownload } from 'bff/hooks/reporting/TransactionDetailApi';

type StatementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  clientDetails: ClientResponseModelMock;
  formValues: StatementFormValues | null;
};
export const StatementModal = (props: StatementModalProps) => {
  return props.isOpen && props.formValues !== null ? <StatementModalComponent {...props} /> : null;
};

const StatementModalComponent = ({
  isOpen,
  onClose,
  clientDetails,
  formValues
}: StatementModalProps) => {
  const statementFormValues = formValues as StatementFormValues;

  const statementType = statementFormValues.statementType || '';
  const testComponentName = `${statementType.replace(/\s+/g, '')}StatementModal'`;

  const [startDate, endDate] = getStartAndEndDate(statementFormValues) ?? [undefined, undefined];

  const { mutateAsync: downloadTransactionSummaryExcel } = useGetTransactionSummaryDownload({
    clientId: 1,
    includeIRR: false,
    planNumbers: ['123'],
    startDate: new Date(2024, 10, 22),
    endDate: new Date(2025, 10, 22)
  });

  const { mutateAsync: downloadTransactionDetailExcel } = useGetTransactionDetailDownload({
    clientId: 1,
    planNumber: '123',
    startDate: new Date(2024, 10, 22),
    endDate: new Date(2025, 10, 22)
  });

  const handleTransactionDetailExcelClick = () => {
    downloadDocumentBackgroundFromBlob({
      downloadFn: async () => {
        const result = await downloadTransactionDetailExcel();
        if (!result) return;
        return {
          blob: result,
          fileName: 'Transaction Detail'
        };
      },
      wording: {
        message:
          'Transaction detail excel sheet will be available in your downloads folder when the download is complete.',
        title: 'Excel sheet downloading...',
        successMessage: 'Transaction detail excel sheet successfully downloaded.',
        errorMessage: 'Transaction detail excel sheet sheet download failed.',
        promiseCompleteTitle: 'Excel document download'
      },
      toastId: 'reporting:transactionDetail'
    });
  };

  const handleTransactionSummaryExcelClick = () => {
    downloadDocumentBackgroundFromBlob({
      downloadFn: async () => {
        const result = await downloadTransactionSummaryExcel();
        if (!result) return;
        return {
          blob: result,
          fileName: 'Transaction Summary'
        };
      },
      wording: {
        message:
          'Transaction summary excel sheet will be available in your downloads folder when the download is complete.',
        title: 'Excel sheet downloading...',
        successMessage: 'Transaction summary excel sheet successfully downloaded.',
        errorMessage: 'Transaction summary excel sheet sheet download failed.',
        promiseCompleteTitle: 'Excel document download'
      },
      toastId: 'reporting:transactionSummary'
    });
  };

  const primaryButton = {
    text: 'Download Excel',
    onClick: () => {
      if (statementType === StatementTypes.transactionDetail) {
        handleTransactionDetailExcelClick();
        return;
      }
      if (statementType === StatementTypes.transactionSummary) {
        handleTransactionSummaryExcelClick();
        return;
      }
    }
  };

  if (formValues === null || startDate === undefined || endDate === undefined) {
    return <SomethingWentWrong />;
  }

  return (
    <div className={styles.fullScreenModalWrapper}>
      <FullScreenModal
        isOpen={isOpen}
        onClose={onClose}
        logo={
          <TakeoverModalPageHeader
            clientName={`${clientDetails.clientFirstName} ${clientDetails.clientLastName}`}
            modalHeading={statementType ? statementType : 'Statement'}
          />
        }
        primaryButton={primaryButton}
        statementType={statementType}
        data-testid={`${testComponentName}FullScreenModal`}
      >
        <div className={styles.contentContainer}>
          <Show when={statementType === StatementTypes.transactionSummary}>
            <TransactionSummaryStatement
              formValues={formValues}
              clientId={clientDetails.clientID}
              startDate={startDate}
              endDate={endDate}
            />
          </Show>
          <Show when={statementType === StatementTypes.fundDetail}>
            <FundDetailStatement
              formValues={formValues}
              clientId={clientDetails.clientID}
              startDate={startDate}
              endDate={endDate}
            />
          </Show>
          <Show when={statementType === StatementTypes.internalRateOfReturnInv}>
            <InternalRateOfReturnInvestmentStatement
              formValues={formValues}
              clientId={clientDetails.clientID}
              startDate={startDate}
              endDate={endDate}
            />
          </Show>
          <Show when={statementType === StatementTypes.transactionDetail}>
            <TransactionDetailStatement
              formValues={formValues}
              clientId={clientDetails.clientID}
              startDate={startDate}
              endDate={endDate}
            />
          </Show>
        </div>
      </FullScreenModal>
    </div>
  );
};
